import React, { useMemo } from 'react';

// Wave Section Divider - Subtle wave transition between sections
export function WaveDivider({ flip = false, className = '' }) {
  return (
    <div className={`w-full overflow-hidden leading-none ${flip ? 'rotate-180' : ''} ${className}`}>
      <svg 
        viewBox="0 0 1200 120" 
        preserveAspectRatio="none" 
        className="w-full h-12 sm:h-16 lg:h-20"
      >
        <path 
          d="M0,60 C200,100 400,20 600,60 C800,100 1000,20 1200,60 L1200,120 L0,120 Z" 
          className="fill-background/50"
        />
        <path 
          d="M0,80 C200,110 400,50 600,80 C800,110 1000,50 1200,80 L1200,120 L0,120 Z" 
          className="fill-background"
        />
      </svg>
    </div>
  );
}

// Seeded random for deterministic particle generation
function seededRandom(seed) {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
}

// Floating Particles Background - Subtle animated dots
export function FloatingParticles({ count = 15, className = '' }) {
  const particles = useMemo(() => {
    return Array.from({ length: count }, (_, i) => ({
      id: i,
      size: seededRandom(i * 1) * 3 + 1,
      left: seededRandom(i * 2) * 100,
      delay: seededRandom(i * 3) * 20,
      duration: 15 + seededRandom(i * 4) * 20,
      opacity: 0.1 + seededRandom(i * 5) * 0.2
    }));
  }, [count]);

  return (
    <div className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`}>
      {particles.map((p) => (
        <div
          key={p.id}
          className="absolute rounded-full bg-primary/30 animate-float-particle"
          style={{
            width: `${p.size}px`,
            height: `${p.size}px`,
            left: `${p.left}%`,
            bottom: '-10px',
            opacity: p.opacity,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
          }}
        />
      ))}
    </div>
  );
}

// Grid Pattern Background - Subtle tech grid
export function GridPattern({ className = '' }) {
  return (
    <div className={`absolute inset-0 pointer-events-none ${className}`}>
      <div className="absolute inset-0 bg-grid-pattern opacity-[0.02]" />
    </div>
  );
}

// Glowing Line Accent - Horizontal accent line with glow
export function GlowLine({ className = '' }) {
  return (
    <div className={`relative h-px w-full max-w-md mx-auto ${className}`}>
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary/30 to-transparent blur-sm" />
    </div>
  );
}

// Section with built-in background effects
export function AnimatedSection({ 
  children, 
  className = '', 
  particles = false, 
  grid = false,
  id = ''
}) {
  return (
    <section id={id} className={`relative overflow-hidden ${className}`}>
      {grid && <GridPattern />}
      {particles && <FloatingParticles count={12} />}
      <div className="relative z-10">
        {children}
      </div>
    </section>
  );
}

// Glowing Card Border on Hover
export function GlowCard({ children, className = '' }) {
  return (
    <div className={`relative group ${className}`}>
      {/* Glow effect on hover */}
      <div className="absolute -inset-0.5 bg-gradient-to-r from-primary/0 via-primary/20 to-primary/0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-sm" />
      <div className="relative">
        {children}
      </div>
    </div>
  );
}

// Animated gradient orb for hero sections
export function GradientOrb({ size = 'md', position = 'top-right', className = '' }) {
  const sizes = {
    sm: 'w-32 h-32',
    md: 'w-64 h-64',
    lg: 'w-96 h-96',
    xl: 'w-[500px] h-[500px]'
  };

  const positions = {
    'top-right': 'top-0 right-0 translate-x-1/3 -translate-y-1/3',
    'top-left': 'top-0 left-0 -translate-x-1/3 -translate-y-1/3',
    'bottom-right': 'bottom-0 right-0 translate-x-1/3 translate-y-1/3',
    'bottom-left': 'bottom-0 left-0 -translate-x-1/3 translate-y-1/3',
    'center': 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'
  };

  return (
    <div 
      className={`absolute ${sizes[size]} ${positions[position]} pointer-events-none ${className}`}
    >
      <div className="w-full h-full rounded-full bg-gradient-to-br from-primary/10 via-cyan-500/5 to-transparent blur-3xl animate-pulse-slow" />
    </div>
  );
}

// Subtle scan line effect
export function ScanLine({ className = '' }) {
  return (
    <div className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`}>
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/[0.02] to-transparent h-32 animate-scan" />
    </div>
  );
}
